import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { US_STATES } from '../constants/locations';
import { ETHNICITIES, EDUCATION_LEVELS, MILITARY_SERVICE_STATUSES } from '../constants/demographics';
import RepresentativeProfileSection from '../components/RepresentativeProfileSection';

function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  
  // Email state
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  const [formData, setFormData] = useState({
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    gender: '',
    ethnicity: '',
    education: '',
    militaryService: '',
    state: '',
    city: '',
    zipCode: ''
  });

  // ZIP code deletion state
  const [showZipDeletePrompt, setShowZipDeletePrompt] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 13 - i);
  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // Load cities for a given state
  const loadCitiesForState = useCallback(async (stateName) => {
    if (!stateName || stateName === 'N/A') {
      setCities([]);
      return;
    }
    
    try {
      setLoadingCities(true);
      const response = await axios.get(`${API_URL}/api/location/cities/${encodeURIComponent(stateName.trim())}`);
      const cityList = response.data.cities || [];
      setCities(cityList);
    } catch (error) {
      console.error('Failed to load cities:', error);
      // Retry once on failure
      try {
        const retry = await axios.get(`${API_URL}/api/location/cities/${encodeURIComponent(stateName.trim())}`);
        setCities(retry.data.cities || []);
      } catch {
        setCities([]);
      }
    } finally {
      setLoadingCities(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cities when state changes — single source of truth for city loading
  useEffect(() => {
    if (formData.state && formData.state !== 'N/A') {
      loadCitiesForState(formData.state);
    } else {
      setCities([]);
    }
  }, [formData.state, loadCitiesForState]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/profile`);
      setProfile(response.data);
      
      const profileState = response.data.state || '';
      const profileCity = response.data.city || '';
      const profileZipCode = response.data.zipCode || '';
      
      if (response.data.birthdate) {
        const [year, month, day] = response.data.birthdate.split('-');
        setFormData({
          birthMonth: parseInt(month),
          birthDay: parseInt(day),
          birthYear: parseInt(year),
          gender: response.data.gender || '',
          ethnicity: response.data.ethnicity || '',
          education: response.data.education || '',
          militaryService: response.data.militaryService || '',
          state: profileState,
          city: profileCity,
          zipCode: profileZipCode
        });
      } else {
        setFormData({
          birthMonth: '',
          birthDay: '',
          birthYear: '',
          gender: response.data.gender || '',
          ethnicity: response.data.ethnicity || '',
          education: response.data.education || '',
          militaryService: response.data.militaryService || '',
          state: profileState,
          city: profileCity,
          zipCode: profileZipCode
        });
      }
      // Cities will be loaded by the useEffect watching formData.state
    } catch (error) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = async (newState) => {
    setFormData(prev => ({ ...prev, state: newState, city: '' }));
    if (newState) {
      await loadCitiesForState(newState);
    } else {
      setCities([]);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSaving(true);

    try {
      const updates = {};
      
      if (formData.birthMonth && formData.birthDay && formData.birthYear) {
        const month = String(formData.birthMonth).padStart(2, '0');
        const day = String(formData.birthDay).padStart(2, '0');
        updates.birthdate = `${formData.birthYear}-${month}-${day}`;
      }
      
      if (formData.gender) {
        updates.gender = formData.gender;
      }
      
      if (formData.ethnicity) {
        updates.ethnicity = formData.ethnicity;
      }
      
      if (formData.education) {
        updates.education = formData.education;
      }

      if (formData.militaryService) {
        updates.military_service = formData.militaryService;
      }
      
      // Add city and state to updates
      if (formData.state !== profile?.state) {
        updates.state = formData.state;
      }
      
      if (formData.city !== profile?.city) {
        updates.city = formData.city;
      }
      
      // Add ZIP code to updates
      if (formData.zipCode !== profile?.zipCode) {
        updates.zipCode = formData.zipCode;
      }

      await axios.put(`${API_URL}/api/profile`, updates);
      setMessage('Profile updated successfully!');
      await loadProfile();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Email handlers
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    setMessage('');
    
    if (!validateEmail(emailInput)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/email/add`, { email: emailInput });
      setMessage(response.data.message);
      setShowEmailForm(false);
      setEmailInput('');
      await loadProfile();
    } catch (error) {
      setEmailError(error.response?.data?.detail || 'Failed to add email');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    setMessage('');
    setEmailSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/email/resend-verification`);
      setMessage(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to resend verification');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleRemoveEmail = async () => {
    if (!window.confirm('Are you sure you want to remove your email? You will not be able to reset your password if you forget it.')) {
      return;
    }
    
    setError('');
    setMessage('');
    setEmailSaving(true);
    try {
      const response = await axios.delete(`${API_URL}/api/email/remove`);
      setMessage(response.data.message);
      await loadProfile();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to remove email');
    } finally {
      setEmailSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const isUSUser = profile?.country === 'United States' || profile?.country === 'US';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
        {message && (
          <div className="bg-green-50 text-primary p-4 rounded-card mb-6 font-rubik">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-danger p-4 rounded-card mb-6 font-rubik">
            {error}
          </div>
        )}

        {/* Email Section */}
        <div className="card-container mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 font-rubik">Email Address</h2>
          <p className="text-sm text-gray-600 mb-4 font-rubik">
            Add an email address to enable password recovery in case you forget your password.
          </p>
          
          {profile?.email ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-800" data-testid="profile-email">{profile.email}</p>
                    {profile.emailVerified ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600" data-testid="email-verified-badge">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-yellow-600" data-testid="email-pending-badge">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        Pending verification
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleRemoveEmail}
                  disabled={emailSaving}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  data-testid="remove-email-btn"
                >
                  Remove
                </button>
              </div>
              
              {!profile.emailVerified && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800">
                      Please check your email inbox and click the verification link. 
                      <button
                        onClick={handleResendVerification}
                        disabled={emailSaving}
                        className="ml-1 underline hover:no-underline font-medium"
                        data-testid="resend-verification-btn"
                      >
                        {emailSaving ? 'Sending...' : 'Resend verification email'}
                      </button>
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {showEmailForm ? (
                <form onSubmit={handleAddEmail} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError('');
                      }}
                      className={`w-full px-4 py-2 border rounded-card focus:ring-2 focus:ring-primary font-rubik ${
                        emailError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="your@email.com"
                      data-testid="email-input"
                      required
                    />
                    {emailError && (
                      <p className="text-red-600 text-xs mt-1" data-testid="email-error">{emailError}</p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={emailSaving}
                      className="btn-secondary py-2 px-4 disabled:bg-gray-400"
                      data-testid="save-email-btn"
                    >
                      {emailSaving ? 'Sending verification...' : 'Add Email'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEmailForm(false);
                        setEmailInput('');
                        setEmailError('');
                      }}
                      className="py-2 px-4 border border-gray-300 rounded-card text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowEmailForm(true)}
                  className="btn-secondary py-2 px-4 flex items-center gap-2"
                  data-testid="add-email-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Email Address
                </button>
              )}
            </>
          )}
        </div>

        {/* Profile Information */}
        <div className="card-container mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 font-rubik">Profile Information</h2>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={profile?.username || ''}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-card bg-gray-100 font-rubik"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                value={profile?.country || ''}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-card bg-gray-100 font-rubik"
              />
            </div>

            {/* Location Section - Editable for US users */}
            {isUSUser ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select
                      value={formData.state}
                      onChange={(e) => handleStateChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                      data-testid="profile-state-select"
                    >
                      <option value="">Select State...</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <select
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      disabled={!formData.state || loadingCities}
                      className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik disabled:bg-gray-100"
                      data-testid="profile-city-select"
                    >
                      <option value="">{loadingCities ? 'Loading cities...' : 'Select City...'}</option>
                      {cities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // If user is trying to clear the ZIP code, show prompt
                      if (!newValue.trim() && formData.zipCode) {
                        setShowZipDeletePrompt(true);
                      } else {
                        setFormData(prev => ({ ...prev, zipCode: newValue }));
                      }
                    }}
                    maxLength={10}
                    className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                    placeholder="Enter ZIP code"
                    data-testid="profile-zipcode-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">ZIP code helps us show you relevant local representatives</p>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={profile?.state || 'N/A'}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-card bg-gray-100 font-rubik"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={profile?.zipCode || 'N/A'}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-card bg-gray-100 font-rubik"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Birthdate</label>
              <div className="grid grid-cols-3 gap-4">
                <select
                  value={formData.birthMonth}
                  onChange={(e) => setFormData({ ...formData, birthMonth: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                  data-testid="birth-month"
                >
                  <option value="">Month</option>
                  {months.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
                <select
                  value={formData.birthDay}
                  onChange={(e) => setFormData({ ...formData, birthDay: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                  data-testid="birth-day"
                >
                  <option value="">Day</option>
                  {days.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <select
                  value={formData.birthYear}
                  onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                  data-testid="birth-year"
                >
                  <option value="">Year</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                data-testid="gender"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Ethnicity</label>
              <select
                value={formData.ethnicity}
                onChange={(e) => setFormData({ ...formData, ethnicity: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                data-testid="ethnicity"
              >
                <option value="">Select Ethnicity</option>
                {ETHNICITIES.map(ethnicity => (
                  <option key={ethnicity} value={ethnicity}>{ethnicity}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Highest Level of Education</label>
              <select
                value={formData.education}
                onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                data-testid="education"
              >
                <option value="">Select Education Level</option>
                {EDUCATION_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Military Service Status</label>
              <select
                value={formData.militaryService}
                onChange={(e) => setFormData({ ...formData, militaryService: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary font-rubik"
                data-testid="military-service"
              >
                <option value="">Select Military Service Status</option>
                {MILITARY_SERVICE_STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full btn-secondary py-3 disabled:bg-gray-400"
              data-testid="save-profile-button"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Representative Profile Section */}
        <div className="mt-8">
          <RepresentativeProfileSection />
        </div>

        {/* ZIP Code Deletion Prompt Modal */}
        {showZipDeletePrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Remove ZIP Code?</h3>
              <p className="text-gray-600 mb-4">
                To remove your ZIP code, you must select your <strong>State</strong> and <strong>City</strong> using the dropdown menus above. 
                This ensures we can still show you relevant local representatives and offices.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Current location:</strong><br />
                  State: {formData.state || 'Not selected'}<br />
                  City: {formData.city || 'Not selected'}
                </p>
              </div>
              {formData.state && formData.city ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowZipDeletePrompt(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, zipCode: '' }));
                      setShowZipDeletePrompt(false);
                    }}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Remove ZIP Code
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Please select both your State and City from the dropdowns above before removing your ZIP code.
                  </p>
                  <button
                    onClick={() => setShowZipDeletePrompt(false)}
                    className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Got it
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
}

export default ProfilePage;
